"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Value = exports.Mod = exports.Sqrt = exports.Power = exports.RoundDown = exports.RoundUp = exports.Even = exports.Odd = exports.Exp = exports.Int = exports.Log = exports.Min = exports.Max = exports.Round = exports.Floor = exports.Ceiling = exports.Average = exports.Abs = exports.Sum = exports.NumericUtilsFunc = void 0;
const lodash_1 = require("lodash");
const basic_1 = require("./basic");
const types_1 = require("../../types");
const utils_1 = require("../../utils");
const params_count_error_1 = require("../../formula_parser/errors/params_count.error");
class NumericFunc extends basic_1.FormulaFunc {
}
NumericFunc.type = types_1.FormulaFuncType.Numeric;
function isArrayParam(params) {
    if (params.length !== 1) {
        return false;
    }
    if (params[0].node.valueType === types_1.BasicValueType.Array) {
        return true;
    }
    return false;
}
function isArrayNodes(nodes) {
    if (!nodes || nodes.length !== 1) {
        return false;
    }
    if (nodes[0].valueType === types_1.BasicValueType.Array) {
        return true;
    }
    return false;
}
/**
  * Tool class for some public functions
  */
class NumericUtilsFunc extends NumericFunc {
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    /**
     * CEILING, FLOOR
     * The output result is aligned with Excel
     */
    static calc2RoundFC(params, calcFn) {
        var _a;
        if (params[0].value == null) {
            return null;
        }
        const value = Number(params[0].value);
        let sign = (_a = params[1]) === null || _a === void 0 ? void 0 : _a.value;
        if (sign != null) {
            sign = Number(sign);
            return (0, utils_1.times)(calcFn((0, utils_1.divide)(value, sign)), sign);
        }
        return calcFn(value);
    }
    // ROUNDUP、ROUNDDOWN
    static calc2RoundDU(params, calcFn1, calcFn2) {
        var _a;
        if (params[0].value == null) {
            return null;
        }
        const value = Number(params[0].value);
        const precision = Math.floor((_a = params[1]) === null || _a === void 0 ? void 0 : _a.value) || 0;
        const offset = Math.pow(10, precision);
        const roundFn = value > 0 ? calcFn1 : calcFn2;
        const rounded = roundFn(value * offset) / offset;
        return rounded;
    }
}
exports.NumericUtilsFunc = NumericUtilsFunc;
class Sum extends NumericFunc {
    static validateParams() {
        //
    }
    static getReturnType() {
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        let result = 0;
        // If there is only one parameter and it is an array, it means that the value of the array type field is summed
        if (isArrayParam(params)) {
            const innerValueType = params[0].node.innerValueType;
            if (innerValueType && innerValueType === types_1.BasicValueType.DateTime) {
                return 0;
            }
            if (!params[0].value) {
                return 0;
            }
            result = params[0].value.reduce((pre, cur) => {
                return (0, utils_1.plus)(pre, (0, utils_1.noNaN)(Number(cur)));
            }, 0);
        }
        else {
            result = params.reduce((pre, cur) => {
                return (0, utils_1.plus)(pre, (0, utils_1.noNaN)(Number(cur.value)));
            }, 0);
        }
        return result;
    }
}
exports.Sum = Sum;
Sum.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
class Abs extends NumericFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return Math.abs(params[0] && params[0].value);
    }
}
exports.Abs = Abs;
class Average extends NumericFunc {
    static validateParams() {
        //
    }
    static getReturnType() {
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        // If there is only one parameter and it is an array, it means that the value of the array type field is summed
        if (isArrayParam(params)) {
            const innerValueType = params[0].node.innerValueType;
            if (innerValueType && innerValueType === types_1.BasicValueType.DateTime) {
                return 0;
            }
            if (!params[0].value) {
                return 0;
            }
            return params[0].value.reduce((pre, cur) => {
                return (0, utils_1.plus)(pre, (0, utils_1.noNaN)(Number(cur)));
            }, 0) / (params[0].value.length || 1);
        }
        const total = params.filter(cur => {
            // Product requirement, the cell is empty, the cell is not counted in the total
            if (cur.value == null) {
                return false;
            }
            return !Number.isNaN(Number(cur.value));
        }).length || 1;
        return params.reduce((pre, cur) => {
            return (0, utils_1.plus)(pre, (0, utils_1.noNaN)(Number(cur.value)));
        }, 0) / total;
    }
}
exports.Average = Average;
Average.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
class Ceiling extends NumericUtilsFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'CEILING', 1);
        }
    }
    static func(params) {
        return this.calc2RoundFC(params, Math.ceil);
    }
}
exports.Ceiling = Ceiling;
class Floor extends NumericUtilsFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'FLOOR', 1);
        }
    }
    static func(params) {
        return this.calc2RoundFC(params, Math.floor);
    }
}
exports.Floor = Floor;
class Round extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'ROUND', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        const num = Number(value);
        const precision = params[1] && Math.floor(params[1].value) || 0;
        const offset = Math.pow(10, precision);
        return (0, utils_1.divide)(Math.round((0, utils_1.times)(num, offset)), offset);
    }
}
exports.Round = Round;
class Max extends NumericFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        if (!params) {
            return types_1.BasicValueType.Number;
        }
        if (isArrayNodes(params)) {
            const innerValueType = params[0].innerValueType;
            if (innerValueType === types_1.BasicValueType.DateTime) {
                return types_1.BasicValueType.DateTime;
            }
            return types_1.BasicValueType.Number;
        }
        // All parameters are date type, return date type
        if (params.every(node => node.valueType === types_1.BasicValueType.DateTime)) {
            return types_1.BasicValueType.DateTime;
        }
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        return this.calc(params, lodash_1.max);
    }
    static calc(params, calcFn) {
        // If there is only one parameter and it is an array, it means that the value of the array type field is summed
        if (isArrayParam(params)) {
            if (!params[0].value) {
                return null;
            }
            const v = calcFn(params[0].value.map(Number).filter(d => !isNaN(d)));
            return v == null ? null : Number(v);
        }
        // If the return value is DateTime, the node of DateTime type should be filtered out, 
        // otherwise the timestamp will be introduced to disturb the calculation result
        if (this.getReturnType(params.map(p => p.node)) !== types_1.BasicValueType.DateTime) {
            params = params.filter(p => p.node.valueType !== types_1.BasicValueType.DateTime);
        }
        const v = calcFn(params.map(p => Number(p.value)).filter(d => !isNaN(d)));
        return v == null ? 0 : Number(v);
    }
}
exports.Max = Max;
Max.acceptValueType = new Set([types_1.BasicValueType.DateTime, types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
// The Min function is different from Max only in the calculation method calcFn
class Min extends Max {
    static func(params) {
        return this.calc(params, lodash_1.min);
    }
}
exports.Min = Min;
class Log extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'LOG', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        const num = Number(value);
        const base = params[1] ? Number(params[1].value) : 10;
        return Math.log(num) / Math.log(base);
    }
}
exports.Log = Log;
class Int extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'INT', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        return Math.floor(value);
    }
}
exports.Int = Int;
class Exp extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'EXP', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (value == null) {
            return null;
        }
        return Math.exp(value);
    }
}
exports.Exp = Exp;
class Odd extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'ODD', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (params[0].value == null) {
            return null;
        }
        const num = Number(value);
        const rounded = value > 0 ? Math.ceil(num) : Math.floor(num);
        if (rounded % 2 !== 0) {
            return rounded;
        }
        return rounded >= 0 ? rounded + 1 : rounded - 1;
    }
}
exports.Odd = Odd;
class Even extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'EVEN', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (params[0].value == null) {
            return null;
        }
        const num = Number(params[0].value);
        const rounded = value > 0 ? Math.ceil(num) : Math.floor(num);
        if (rounded % 2 === 0) {
            return rounded;
        }
        return rounded > 0 ? rounded + 1 : rounded - 1;
    }
}
exports.Even = Even;
class RoundUp extends NumericUtilsFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'ROUNDUP', 1);
        }
    }
    static func(params) {
        return this.calc2RoundDU(params, Math.ceil, Math.floor);
    }
}
exports.RoundUp = RoundUp;
class RoundDown extends NumericUtilsFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'ROUNDDOWN', 1);
        }
    }
    static func(params) {
        return this.calc2RoundDU(params, Math.floor, Math.ceil);
    }
}
exports.RoundDown = RoundDown;
class Power extends NumericFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'POWER', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (params[0].value == null) {
            return null;
        }
        return Math.pow(value, params[1].value);
    }
}
exports.Power = Power;
class Sqrt extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'SQRT', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (params[0].value == null) {
            return null;
        }
        return Math.sqrt(value);
    }
}
exports.Sqrt = Sqrt;
class Mod extends NumericFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'MOD', 2);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const value = params[0].value;
        if (params[0].value == null) {
            return null;
        }
        const num = Number(value);
        const divisor = Number(params[1].value);
        const mod = num % divisor;
        if ((num ^ divisor) < 0) {
            return mod * (-1);
        }
        return mod;
    }
}
exports.Mod = Mod;
class Value extends NumericFunc {
    static validateParams(params) {
        if (params.length < 1) {
            throw new params_count_error_1.ParamsCountError(params_count_error_1.ParamsErrorType.AtLeastCount, 'VALUE', 1);
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const text = String(params[0].value);
        const regNumber = /[^0-9.+-]/g;
        const regSymbol = /(\+|-|\.)+/g;
        const value = text
            .replace(regNumber, '')
            .replace(regSymbol, '$1');
        return parseFloat(value);
    }
}
exports.Value = Value;
//# sourceMappingURL=numeric.js.map